import * as Yup from 'yup';
import { useRef } from 'react';
import { of, Subscription, timer } from 'rxjs';
import { catchError, switchMap, tap, finalize } from 'rxjs/operators';
import { fromPromise } from 'rxjs/internal-compatibility';
import { AxiosError } from 'axios';
import { useAsyncValidationForm } from './useAsyncValidationForm';
import { AsyncValidationStatus } from '../interfaces/async-validation-form';
import { getErrorMessageFromAxiosError } from '../../../../request/utils';

export function useAsyncFieldTest<K extends string, V>({
  fieldName,
  skipValidationOnStoredValue,
  storedValue,
  isRequired,
  requiredMessage,
  validator,
  defaultValidationError,
  extraPrams
}: {
  fieldName: K;
  skipValidationOnStoredValue?: boolean;
  storedValue?: V;
  isRequired: boolean;
  requiredMessage?: string;
  validator: (
    value: V,
    signal: AbortSignal,
    extraParams?: object
  ) => Promise<{
    isValid: boolean;
    reason?: string;
  }>;
  defaultValidationError?: string | ((val: V | undefined) => string);
  extraPrams?: object;
}) {
  const { setFieldStatus, fields } = useAsyncValidationForm<{ [key: string]: unknown }>();
  const validationSubscription = useRef(Subscription.EMPTY);
  const cachedError = useRef<string | undefined>();

  return async (value: V | undefined) => {
    const status = fields[fieldName];

    if (status === AsyncValidationStatus.PENDING) {
      validationSubscription.current.unsubscribe();
    }

    if (isRequired && (typeof value === 'undefined' || value === null || value === '')) {
      setFieldStatus(fieldName, AsyncValidationStatus.INVALID);
      return new Yup.ValidationError(requiredMessage!, value, fieldName as string);
    }

    if (
      skipValidationOnStoredValue &&
      typeof storedValue !== 'undefined' &&
      storedValue !== null &&
      storedValue === value
    ) {
      setFieldStatus(fieldName, AsyncValidationStatus.VALID);
      return true;
    }

    if (status !== AsyncValidationStatus.PENDING) {
      if (status === AsyncValidationStatus.ERRORED || status === AsyncValidationStatus.INVALID) {
        return new Yup.ValidationError(cachedError.current!, value, fieldName as string);
      }

      return true;
    }

    return new Promise<boolean | Yup.ValidationError>(resolve => {
      setFieldStatus(fieldName, AsyncValidationStatus.IN_PROGRESS);

      const abortController = new AbortController();

      validationSubscription.current = timer(500)
        .pipe(
          switchMap(() => fromPromise(validator(value!, abortController.signal, extraPrams))),
          tap(({ isValid, reason }) => {
            setFieldStatus(
              fieldName,
              isValid ? AsyncValidationStatus.VALID : AsyncValidationStatus.INVALID
            );

            if (isValid) {
              resolve(true);
              return;
            }

            const finalReason =
              reason ||
              (typeof defaultValidationError === 'string'
                ? defaultValidationError
                : defaultValidationError?.(value) || `${fieldName} not valid`);

            cachedError.current = finalReason;

            resolve(new Yup.ValidationError(finalReason, value, fieldName as string));
          }),
          catchError(error => {
            setFieldStatus(fieldName, AsyncValidationStatus.ERRORED);

            resolve(
              new Yup.ValidationError(
                getErrorMessageFromAxiosError(error as AxiosError),
                value,
                fieldName as string
              )
            );

            return of();
          }),
          finalize(() => {
            abortController.abort();
          })
        )
        .subscribe();
    });
  };
}
